/**
 * Cache Utility for API Response Caching
 * 
 * Features:
 * - In-memory caching with TTL (Time To Live)
 * - Automatic expiration of stale data
 * - Pattern-based cache clearing
 * - Type-safe cache operations
 */

interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
}

export class CacheManager {
  private cache: Map<string, CacheEntry> = new Map();
  private readonly defaultTTL: number;

  constructor(ttlInMilliseconds: number = 5 * 60 * 1000) {
    this.defaultTTL = ttlInMilliseconds;
  }

  /**
   * Generates a unique cache key from endpoint and parameters
   * @param endpoint - The API endpoint or cache namespace
   * @param params - Parameters to include in the cache key
   * @returns A unique cache key string
   */
  getCacheKey(endpoint: string, params?: any): string {
    if (!params || Object.keys(params).length === 0) {
      return endpoint;
    }
    return `${endpoint}_${JSON.stringify(params)}`;
  }

  /**
   * Retrieves cached data if it exists and is not expired
   * @param key - The cache key
   * @param ttl - Optional custom TTL (overrides default)
   * @returns Cached data or null if not found/expired
   */
  get<T = any>(key: string, ttl?: number): T | null {
    const cached = this.cache.get(key);
    if (!cached) {
      return null;
    }

    const now = Date.now();
    const maxAge = ttl !== undefined ? ttl : this.defaultTTL;

    if (now - cached.timestamp > maxAge) {
      // Cache expired
      this.cache.delete(key);
      return null;
    }

    return cached.data as T;
  }

  /**
   * Stores data in cache with current timestamp
   * @param key - The cache key
   * @param data - The data to cache
   */
  set<T = any>(key: string, data: T): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  /**
   * Checks if a key exists in cache and is not expired
   * @param key - The cache key
   * @param ttl - Optional custom TTL
   * @returns True if cache exists and is valid
   */
  has(key: string, ttl?: number): boolean {
    return this.get(key, ttl) !== null;
  }

  /**
   * Removes a specific cache entry
   * @param key - The cache key to remove
   * @returns True if entry was removed, false if it didn't exist
   */
  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  /**
   * Clears all cache entries
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Clears cache entries matching a pattern
   * @param pattern - String pattern to match against cache keys
   */
  clearByPattern(pattern: string): void {
    const keysToDelete: string[] = [];
    this.cache.forEach((_, key) => {
      if (key.includes(pattern)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Gets the current cache size
   * @returns Number of cached entries
   */
  size(): number {
    return this.cache.size;
  }

  /**
   * Gets all cache keys
   * @returns Array of cache keys
   */
  keys(): string[] {
    return Array.from(this.cache.keys());
  }

  /**
   * Gets cache statistics
   * @returns Object with cache statistics
   */
  getStats(): {
    size: number;
    keys: string[];
    oldestEntry: number | null;
    newestEntry: number | null;
  } {
    const timestamps: number[] = [];
    this.cache.forEach(entry => timestamps.push(entry.timestamp));

    return {
      size: this.cache.size,
      keys: this.keys(),
      oldestEntry: timestamps.length > 0 ? Math.min(...timestamps) : null,
      newestEntry: timestamps.length > 0 ? Math.max(...timestamps) : null,
    };
  }

  /**
   * Removes all expired entries
   * @param ttl - Optional custom TTL
   * @returns Number of entries removed
   */
  cleanExpired(ttl?: number): number {
    const now = Date.now();
    const maxAge = ttl !== undefined ? ttl : this.defaultTTL;
    const keysToDelete: string[] = [];

    this.cache.forEach((entry, key) => {
      if (now - entry.timestamp > maxAge) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => this.cache.delete(key));
    return keysToDelete.length;
  }
}

/**
 * Global cache instance with 5-minute TTL
 * Can be imported and used across different services
 */
export const globalCache = new CacheManager(5 * 60 * 1000);

/**
 * Creates a cached version of an async function
 * @param fn - The async function to cache
 * @param cacheManager - The cache manager to use
 * @param keyGenerator - Function to generate cache key from arguments
 * @param ttl - Optional custom TTL
 * @returns Cached version of the function
 */
export function withCache<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  cacheManager: CacheManager,
  keyGenerator: (...args: Parameters<T>) => string,
  ttl?: number
): T {
  return (async (...args: Parameters<T>) => {
    const key = keyGenerator(...args);
    
    // Check cache first
    const cached = cacheManager.get(key, ttl);
    if (cached !== null) {
      return cached;
    }

    // If not in cache, call the function
    const result = await fn(...args);
    
    // Store in cache
    cacheManager.set(key, result);
    
    return result;
  }) as T;
}

/**
 * Decorator for caching method results
 * Usage: @Cached('methodName', cacheManager)
 */
export function Cached(namespace: string, cacheManager: CacheManager, ttl?: number) {
  return function (
    target: any,
    propertyKey: string,
    descriptor: PropertyDescriptor
  ) {
    const originalMethod = descriptor.value;

    descriptor.value = async function (...args: any[]) {
      const key = cacheManager.getCacheKey(`${namespace}_${propertyKey}`, args);
      
      const cached = cacheManager.get(key, ttl);
      if (cached !== null) {
        return cached;
      }

      const result = await originalMethod.apply(this, args);
      cacheManager.set(key, result);
      
      return result;
    };

    return descriptor;
  };
}
